/* ============================================================
   AddBrandChainBridge — drives the "new brand → research → content
   plan" chain APP-WIDE, so it never depends on the add-brand modal
   staying open. AddBrandFlow writes a localStorage marker at create
   time; this bridge (mounted next to ResearchActivityBridge) picks it
   up, polls the new brand's research run, and when it turns ready
   kicks the plan build once — surfacing the build in the global
   generation plate. Survives navigation AND full reloads (the marker
   persists; an interrupted plan kick is re-checked on mount).
   Renders nothing.
   ============================================================ */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api/client.js';
import { multiBrandEnabled } from '../lib/flags.js';
import { useBrand } from '../contexts/BrandContext.jsx';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';

export const CHAIN_KEY = 'dathent.addbrand.chain';

export function readChainMarker() {
  try {
    const raw = window.localStorage.getItem(CHAIN_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw);
    return m && m.brandId ? m : null;
  } catch (_) {
    return null;
  }
}

export function writeChainMarker(marker) {
  try {
    if (!marker) window.localStorage.removeItem(CHAIN_KEY);
    else window.localStorage.setItem(CHAIN_KEY, JSON.stringify(marker));
  } catch (_) { /* storage unavailable — chain degrades to the modal-only flow */ }
}

const POLL_MS = 12000;
const DONE_TTL_MS = 60 * 60 * 1000; // clean finished markers after an hour

// Auto-plan bookkeeping: research ids whose plan-build has already been
// handled (kicked or found grounded) — one attempt per research run, ever.
const AUTOPLAN_DONE_KEY = 'dathent.autoplan.done';
const AUTOPLAN_KICK_KEY = 'dathent.autoplan.kick';

function readDoneIds() {
  try { const a = JSON.parse(window.localStorage.getItem(AUTOPLAN_DONE_KEY) || '[]'); return Array.isArray(a) ? a : []; } catch (_) { return []; }
}
function markDoneId(id) {
  try {
    const a = readDoneIds().filter((x) => x !== id);
    a.push(id);
    window.localStorage.setItem(AUTOPLAN_DONE_KEY, JSON.stringify(a.slice(-20)));
  } catch (_) { /* non-fatal */ }
}

export default function AddBrandChainBridge() {
  const { t } = useTranslation();
  const brand = useBrand();
  const brandRef = useRef(brand);
  brandRef.current = brand;
  const { startOperation, completeOperation } = useGenerationStatus();
  const busyRef = useRef(false); // in-flight plan kick guard (per tab)

  useEffect(() => {
    if (!multiBrandEnabled()) return undefined;
    let alive = true;
    let timer = null;

    const kickPlan = async (m) => {
      if (busyRef.current) return;
      busyRef.current = true;
      writeChainMarker({ ...m, stage: 'plan', plannedAt: Date.now() });
      const opId = `addbrand_plan_${m.brandId}`;
      startOperation({
        id: opId,
        kind: 'strategy',
        label: t('addBrand.planOpLabel', { name: m.name, defaultValue: 'Building content plan — {{name}}' }),
        status: 'running',
        total: null,
        deeplink: '/app/plan',
        sourceRef: { type: 'job', id: m.brandId },
        expectedDurationMs: 2 * 60000,
      });
      try {
        // Explicit brandProfileId (spread-last in _withBrand → wins over the
        // active brand) so switching brands mid-chain can't mistarget the plan.
        await api.strategy.generate({ confirmCreditSpend: true, brandProfileId: m.brandId });
        writeChainMarker({ ...m, stage: 'done', doneAt: Date.now() });
        completeOperation(opId, { status: 'succeeded' });
      } catch (err) {
        // Same stand-down as autoPlanTick: a 409 means the server chain owns
        // this build, which is exactly what we want — not a failed chain.
        if (err?.status === 409) {
          writeChainMarker({ ...m, stage: 'done', doneAt: Date.now() });
          completeOperation(opId, { status: 'succeeded' });
        } else {
          writeChainMarker({ ...m, stage: 'failed', failedStage: 'plan', doneAt: Date.now() });
          completeOperation(opId, { status: 'failed', error: t('addBrand.pipeFailed', { defaultValue: 'Failed' }) });
        }
      } finally {
        busyRef.current = false;
      }
    };

    // Owner default (2026-07-20): after ANY research run completes for the
    // active brand, the content plan rebuilds automatically — no extra click.
    // One attempt per research id (kicked or found already-grounded), with a
    // 3-minute cross-tab guard around the metered generate call. A failed
    // build simply leaves the existing manual "Rebuild plan" nudge in place.
    const autoPlanTick = async () => {
      const b = brandRef.current;
      if (!b?.enabled || !b.activeBrandId || busyRef.current) return;
      const m = readChainMarker();
      if (m && (m.stage === 'research' || m.stage === 'plan')) return; // add-brand chain owns it
      try {
        if (document.visibilityState !== 'visible') return;
        const res = await api.research.list();
        const latest = ((res?.data ?? res)?.reports || []).find((r) => r.status === 'ready');
        if (!latest || readDoneIds().includes(latest.id)) return;
        // Cross-tab kick guard.
        try {
          const k = JSON.parse(window.localStorage.getItem(AUTOPLAN_KICK_KEY) || 'null');
          if (k && k.id === latest.id && Date.now() - k.ts < 180000) return;
        } catch (_) { /* unreadable guard — proceed */ }
        // Already grounded on this research (manual rebuild, add-brand chain,
        // or an earlier pass)? Then just record it.
        let grounded = null;
        let hasStrategy = false;
        try {
          const sres = await api.strategy.getFresh();
          const strat = sres?.data?.strategy || sres?.strategy || sres?.data || null;
          hasStrategy = Boolean(strat && (strat.id || strat.strategy));
          grounded = strat?.researchId ?? strat?.strategy?.researchId ?? null;
        } catch (_) { /* no strategy yet */ }
        if (hasStrategy && grounded === latest.id) { markDoneId(latest.id); return; }
        try { window.localStorage.setItem(AUTOPLAN_KICK_KEY, JSON.stringify({ id: latest.id, ts: Date.now() })); } catch (_) { /* best-effort */ }
        busyRef.current = true;
        const opId = `autoplan_${latest.id}`;
        startOperation({
          id: opId,
          kind: 'strategy',
          label: t('addBrand.planOpLabel', { name: b.activeBrand?.name || '', defaultValue: 'Building content plan — {{name}}' }),
          status: 'running',
          total: null,
          deeplink: '/app/plan',
          sourceRef: { type: 'job', id: latest.id },
          expectedDurationMs: 2 * 60000,
        });
        try {
          await api.strategy.generate({ confirmCreditSpend: true, brandProfileId: b.activeBrandId });
          completeOperation(opId, { status: 'succeeded' });
        } catch (err) {
          // 409 plan_rebuild_in_progress is the DESIRED outcome, not a failure:
          // the server-side activation chain is already building this very plan
          // (it now publishes a plan_regenerate op), so we simply stand down
          // instead of racing it into a second paid build. Anything else is a
          // real failure and keeps the manual "Rebuild plan" nudge in place.
          const standDown = err?.status === 409;
          completeOperation(opId, standDown
            ? { status: 'succeeded' }
            : { status: 'failed', error: t('addBrand.pipeFailed', { defaultValue: 'Failed' }) });
        } finally {
          markDoneId(latest.id);
          busyRef.current = false;
        }
      } catch (_) { /* transient — next tick */ }
    };

    const tick = async () => {
      await autoPlanTick();
      const m = readChainMarker();
      if (!m) return;
      // Housekeeping: drop finished markers after their display TTL.
      if ((m.stage === 'done' || m.stage === 'failed') && m.doneAt && Date.now() - m.doneAt > DONE_TTL_MS) {
        writeChainMarker(null);
        return;
      }
      if (m.stage === 'plan') {
        // A kick is (or was) in flight — possibly in ANOTHER tab or before a
        // reload. Only reconcile once it's old enough that the original kick
        // must have finished or died (metered call — never double-fire early).
        if (m.plannedAt && Date.now() - m.plannedAt < 180000) return;
        try {
          const res = await api.strategy.get({ brandProfileId: m.brandId });
          const has = Boolean((res?.data ?? res)?.strategy || (res?.data ?? res)?.id);
          if (has) writeChainMarker({ ...m, stage: 'done', doneAt: Date.now() });
          else await kickPlan(m);
        } catch (_) {
          await kickPlan(m);
        }
        return;
      }
      if (m.stage !== 'research') return;
      try {
        if (document.visibilityState !== 'visible') return;
        const res = await api.research.list({ brandProfileId: m.brandId });
        const target = ((res?.data ?? res)?.reports || []).find((r) => r.brandProfileId === m.brandId);
        if (!alive || !target) return;
        if (target.status === 'failed') {
          writeChainMarker({ ...m, stage: 'failed', failedStage: 'research', doneAt: Date.now() });
        } else if (target.status === 'ready') {
          await kickPlan(m);
        }
      } catch (_) { /* transient — next tick */ }
    };

    const loop = async () => {
      await tick();
      if (!alive) return;
      // Fast cadence only while an add-brand chain is mid-flight; the general
      // auto-plan watcher idles at a minute so it doesn't double the research
      // polling ResearchActivityBridge already does.
      const m = readChainMarker();
      const fast = m && (m.stage === 'research' || m.stage === 'plan');
      timer = setTimeout(loop, fast ? POLL_MS : 60000);
    };
    loop();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [t, startOperation, completeOperation]);

  return null;
}
